<template>
  <div>
    <v-dialog
      :value="value"
      max-width="900"
      width="100%"
      persistent
      scrollable
      content-class="feature-access-dialog"
      @input="$emit('input', $event)"
    >
      <v-card>
        <v-card-title
          class="px-6 py-4 d-flex justify-space-between mb-5"
          :class="$vuetify.breakpoint.smAndDown ? 'align-start' : 'align-center'"
        >
          <div class="flex-grow-1" style="width: 80%">
            <div class="py-1 primary--text font-weight-bold">
              {{ $vuetify.lang.t("$vuetify.user_access_control_permission_feature.feature_access_modal_title") }}
            </div>

            <div class="text-body-2 grey--text text--darken-1"  :class="{ 'mt-2': !$vuetify.breakpoint.smAndDown }">
              {{ $vuetify.lang.t("$vuetify.user_access_control_permission_feature.feature_access_modal_description") }}
            </div>
          </div>

          <v-btn icon :disabled="loading" @click="closeModal" class="ml-2 flex-shrink-0">
            <v-icon size="28" color="grey darken-1"> mdi-close </v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-0 overflow-y-auto" style="max-height: 60vh">
          <v-expansion-panels v-if="localFeatures.length" v-model="expandedPanels" multiple flat>
            <v-expansion-panel v-for="(feature, index) in localFeatures" :key="feature.key">
              <v-divider v-if="index !== 0" :key="`divider-${feature.key}`" />
              <v-expansion-panel-header hide-actions class="default-cursor">
                <div
                  class="d-flex align-center"
                  :class="{ 'align-start': $vuetify.breakpoint.smAndDown }"
                  style="width: 100%"
                  @click.stop
                >
                  <v-icon
                    v-if="feature.sections.length"
                    class="mr-4"
                    style="cursor: pointer"
                    @click="toggleExpand(index)"
                  >
                    {{ expandedPanels.includes(index) ? "mdi-chevron-up" : "mdi-chevron-down" }}
                  </v-icon>

                  <v-icon v-else class="mr-4" style="visibility: hidden"> mdi-chevron-right </v-icon>

                  <div class="flex-grow-1 min-width-0">
                    <div class="subtitle-1 font-weight-medium">
                      {{ $vuetify.lang.current === "ja" ? feature.label_jp : feature.label_en }}
                    </div>

                    <div class="caption grey--text">
                      {{ getFeatureDescription(feature) }}
                    </div>
                  </div>

                  <div @click.stop>
                    <v-switch v-model="feature.enabled" inset hide-details class="ma-0 pa-0 small-switch" />
                  </div>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content v-if="feature.sections.length">
                <div v-for="section in feature.sections" :key="section.key">
                  <v-divider :key="`divider-${feature.key}-${section.key}`" class="ml-8" />

                  <div
                    class="d-flex align-center justify-space-between py-4"
                     :class="$vuetify.breakpoint.smAndDown ? 'px-5 align-start' : 'px-8'"
                  >
                    <div class="pl-12" :class="{ 'pl-8': $vuetify.breakpoint.smAndDown }">
                      <div class="subtitle-2">
                        {{ $vuetify.lang.current === "ja" ? section.label_jp : section.label_en }}
                      </div>

                      <div class="caption grey--text">
                        {{ getSectionDescription(section) }}
                      </div>
                    </div>

                    <v-switch
                      :input-value="feature.enabled ? section.enabled : false"
                      :disabled="!feature.enabled"
                      hide-details
                      inset
                      class="ma-0 pa-0 small-switch mr-6"
                      :class="{ 'mr-6': !$vuetify.breakpoint.smAndDown }"
                      @change="section.enabled = $event"
                    />
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <div v-else class="text-center pa-8 grey--text">
            {{
              $vuetify.lang.t("$vuetify.user_access_control_permission_feature.no_feature_access_configuration_found")
            }}
          </div>
        </v-card-text>

        <v-divider />
        <v-card-actions class="px-6 py-3" :class="$vuetify.breakpoint.smAndDown ? 'flex-column align-stretch' : ''">
          <v-btn
            outlined
            color="grey darken-1"
            class="text-uppercase font-weight-medium"
            :disabled="loading"
            @click="resetChangesConfirmModal(true)"
          >
            {{ $vuetify.lang.t("$vuetify.user_access_control_permission_feature.reset_changes") }}
          </v-btn>

          <v-spacer v-if="!$vuetify.breakpoint.smAndDown" />

          <div :class="$vuetify.breakpoint.smAndDown ? 'd-flex mt-3' : 'd-flex'">
            <v-btn :disabled="loading" outlined color="error" class="flex-grow-1" @click="closeModal">
              {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
            </v-btn>

            <v-btn
              color="primary"
              :loading="loading"
              :disabled="!localFeatures.length || loading"
              class="flex-grow-1 ml-2"
              @click="submitForm"
            >
              {{ $vuetify.lang.t("$vuetify.user_access_control_permission_feature.save_changes") }}
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <DialogModal
      v-model="openResetConfirmModal"
      :title="$vuetify.lang.t('$vuetify.user_access_control_permission_feature.confirm_modal.title')"
      :message="$vuetify.lang.t('$vuetify.user_access_control_permission_feature.confirm_modal.description')"
      :confirm-button-text="$vuetify.lang.t('$vuetify.common.confirm_btn')"
      :cancel-button-text="$vuetify.lang.t('$vuetify.common.cancel_btn')"
      @onConfirmation="confirmResetFeatures()"
      @onCancel="openResetConfirmModal = false"
    />
  </div>
</template>

<script>
import DialogModal from "@/components/common/DialogModal";
export default {
  name: "FeatureAccessModal",
  components: {
    DialogModal,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },

    title: {
      type: String,
      default: "",
    },

    userName: {
      type: String,
      default: "",
    },

    featureConfig: {
      type: Object,
      default: () => ({
        features: {},
      }),
    },

    loading: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "edit", // edit | assign
    },
  },

  data() {
    return {
      localFeatures: [],
      expandedPanels: [],
      openResetConfirmModal: false,
    };
  },

  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.setLocalFeatureConfig();
        }
      },
    },

    featureConfig: {
      deep: true,
      handler() {
        if (this.value) {
          this.setLocalFeatureConfig();
        }
      },
    },
  },

  methods: {
    setLocalFeatureConfig() {
      const features = this.featureConfig?.features || {};
      const defaultFeatures =
      this.$store.state.featureAccess.defaultFeatureConfig || {};
      const currentSolution = (this.$route.query.stype || "").toLowerCase();
      this.localFeatures = Object.entries(features)
        .map(([featureKey, feature]) => {
          const defaultFeature = defaultFeatures[featureKey] || {};

          const sections = Object.entries(feature.sections || {})
            .map(([sectionKey, section]) => {
              const defaultSection =
                defaultFeature.sections?.[sectionKey] || {};

              return {
                key: sectionKey,
                ...defaultSection,
                ...section,
              };
            })
            .filter(section => {
              return (
                !currentSolution ||
                !section.solutions ||
                section.solutions.includes(currentSolution)
              );
            })
            .sort(
              (a, b) =>
                (a.sequence ?? Number.MAX_SAFE_INTEGER) -
                (b.sequence ?? Number.MAX_SAFE_INTEGER)
            );

          return {
            key: featureKey,
            ...defaultFeature,
            ...feature,
            sections,
          };
        })
        .filter(feature => {
          return (
            !currentSolution ||
            !feature.solutions ||
            feature.solutions.includes(currentSolution)
          );
        })
        .sort(
          (a, b) =>
            (a.sequence ?? Number.MAX_SAFE_INTEGER) -
            (b.sequence ?? Number.MAX_SAFE_INTEGER)
        );

      this.expandedPanels = [];
    },

    getFeatureDescription(feature) {
      const featureName = this.$vuetify.lang.current === "ja" ? feature.label_jp : feature.label_en;

      return this.$vuetify.lang.t("$vuetify.user_access_control_permission_feature.feature_description", featureName);
    },

    getSectionDescription(section) {
      const sectionName = this.$vuetify.lang.current === "ja" ? section.label_jp : section.label_en;
      return this.$vuetify.lang.t("$vuetify.user_access_control_permission_feature.section_description", sectionName);
    },

    convertFeaturesToObject() {
      return this.localFeatures.reduce((features, feature) => {
        features[feature.key] = {
          enabled: feature.enabled,
          label_en: feature.label_en,
          label_jp: feature.label_jp,
          sections: feature.sections.reduce((sections, section) => {
            sections[section.key] = {
              enabled: section.enabled,
              label_en: section.label_en,
              label_jp: section.label_jp,
            };

            return sections;
          }, {}),
        };

        return features;
      }, {});
    },

    submitForm() {
      this.$emit("save", {
        features: this.convertFeaturesToObject(),
      });
    },

    closeModal() {
      this.$emit("input", false);
      this.$emit("close");
    },
    toggleExpand(index) {
      const i = this.expandedPanels.indexOf(index);
      if (i === -1) {
        this.expandedPanels.push(index);
      } else {
        this.expandedPanels.splice(i, 1);
      }
    },
    resetFeatures() {
      this.localFeatures.forEach(feature => {
        feature.enabled = true;

        feature.sections.forEach(section => {
          section.enabled = true;
        });
      });
    },
    resetChangesConfirmModal(value) {
      this.openResetConfirmModal = value;
    },
    confirmResetFeatures() {
      this.resetFeatures();
      this.openResetConfirmModal = false;
    },
  },
};
</script>

<style scoped>
.v-expansion-panel--active:not(:first-child),
.v-expansion-panel--active + .v-expansion-panel {
  margin-top: 0 !important;
}
::v-deep(.small-switch) {
  transform: scale(0.8);
  transform-origin: right center;
}
::v-deep .v-expansion-panel-header {
  cursor: default !important;
}

::v-deep .v-expansion-panel-content__wrap {
  padding: 0;
}

.min-width-0 {
  min-width: 0;
}

@media (max-width: 960px) {
  ::v-deep .v-expansion-panel-header {
    padding-left: 16px;
    padding-right: 16px;
  }
  ::v-deep .feature-access-dialog {
    margin: 12px !important;
    width: calc(100% - 24px) !important;
  }
}

@media (max-width: 600px) {
  ::v-deep .v-card__title {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }

  ::v-deep .v-card__actions {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }

  ::v-deep .v-card-text::-webkit-scrollbar,
  ::v-deep .v-card__text::-webkit-scrollbar {
    width: 3px;
  }

}
</style>
